import { configureStore } from '@reduxjs/toolkit'
import { setupListeners } from '@reduxjs/toolkit/query'
import { Selector } from 'react-redux'
import { combineReducers } from 'redux'
import { FLUSH, PAUSE, PERSIST, persistReducer, persistStore, PURGE, REGISTER, REHYDRATE } from 'redux-persist'
import { PersistPartial } from 'redux-persist/lib/persistReducer'
import { userReducer } from './auth/user/reducer'
import { cardScanReducer } from './cardScan/reducer'
import { disputeReducer } from './dispute/reducer'
import { consumerReducer } from './account/consumer/reducer'
import { identityReducer } from './identity/reducer'
import { memoize } from '../utils/memoize'
import { DEBUG_MODE } from '../env'
import { apiService } from '@afterpaytouch/portal-api/api'
import { apiService as topazService } from '@afterpaytouch/topaz-api/api'
import { apiService as shopService } from '@afterpaytouch/shop-api/api'
import { paymentService, paymentHttpService } from './payments'
import applePayReducer from './applepay/reducer'
import { registrationReducer } from './registration/reducer'
import { persistConfig } from './persist-config'

const { reducer: paymentReducer, middleware: paymentMiddleware } = paymentService

const { reducer: topazReducer, middleware: topazMiddleware } = topazService.service

const { reducer: shopReducer, middleware: shopMiddleware } = shopService.service

const { reducer: apiReducer, middleware: apiMiddleware } = apiService.service

export const combinedReducer = combineReducers({
  auth: combineReducers({
    user: userReducer,
  }),
  applepay: applePayReducer,
  // @TODO payments reducer to go here that should include cards, bankAccounts and applePay
  cardScan: cardScanReducer,
  dispute: disputeReducer,
  [paymentService.reducerPath]: paymentReducer,
  [apiService.service.reducerPath]: apiReducer,
  [topazService.service.reducerPath]: topazReducer,
  [shopService.service.reducerPath]: shopReducer,
  consumer: consumerReducer,
  registration: registrationReducer,
  identity: identityReducer,
})

type RequestModule = typeof import('./request')

// eslint-disable-next-line  @typescript-eslint/no-require-imports
const loadRequestModule = (): RequestModule => require('./request')

const apis = {
  portalApi: memoize(() => loadRequestModule().createPortalApi()),
  topaz: memoize(() => loadRequestModule().createTopazApi()),
  shop: memoize(() => loadRequestModule().createShopApi()),
  payments: memoize(() => paymentHttpService),
}

// SSR safety: Only apply persistence on client-side
const ssr = typeof window === 'undefined'

// Apply persistReducer only on client-side to avoid SSR hydration issues
const reducer = ssr ? combinedReducer : persistReducer(persistConfig, combinedReducer)

// Don't need to put topaz middleware in the extraArgument because
// we are not planning to call it manually, instead, put the RTKQ middleware
export const store = configureStore({
  reducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      thunk: { extraArgument: apis },
      serializableCheck: {
        // Ignore redux-persist actions in serializability check
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(apiMiddleware, topazMiddleware, shopMiddleware, paymentMiddleware),
  devTools: DEBUG_MODE,
})

setupListeners(store.dispatch)

// Create persistor only on client-side for SSR safety
export const persistor = ssr ? null : persistStore(store)

type ConsumerReducerType = typeof combinedReducer & Partial<PersistPartial>

export type ConsumerAppStore = typeof store
export type ConsumerAppState = ReturnType<ConsumerReducerType>
export type ConsumerAppDispatch = ConsumerAppStore['dispatch']
export type ConsumerAppSelector<T> = Selector<ConsumerAppState, T>
